import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import css from './index.module.scss';

export type TabItem = {
    title: string;
    href: string;
};

interface TabProps {
    list: TabItem[];
    sticky?: boolean;
    stickyHeight?: number;
}

const Tabs: React.FC<TabProps> = ({ list, sticky = false, stickyHeight = 0 }) => {
    const [activeIndex, setActiveIndex] = useState(list[0].href);

    const stopScroll = useRef(false);
    const timer = useRef<NodeJS.Timeout | null>(null);
    const handleChange = (val: string) => {
        setActiveIndex(val);
        // 改变 url，触发路由变化
        window.location.hash = '';
        window.location.hash = `#${val}`;

        // 点击的时候先屏蔽滚动对齐 tab 功能
        stopScroll.current = true;
        timer.current && clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            stopScroll.current = false;
        }, 1000);

        alignTab(val);
    };

    // tab 左右对齐方法
    const tabsRef = useRef<HTMLDivElement>(null);
    const alignTab = (val: string) => {
        // h5 情况下，点击 tab 后，剧中展示 tab
        const tabItem = document.querySelector(`.tab-item-${val}`) as HTMLDivElement;
        if (tabsRef.current && tabItem) {
            const centerNum = tabItem.offsetLeft - tabsRef.current.offsetWidth / 2 + tabItem.offsetWidth / 2;
            tabsRef.current.scrollTo({ left: centerNum, behavior: 'smooth' });
        }
    };

    // 滚动条滚动时，对齐 tab
    useEffect(() => {
        const offset = 0;
        const ids = list.map((i) => i.href);
        const tops = ids.map((i) => {
            const moduleEl = document.querySelector(`#${i}`) as HTMLElement;
            if (moduleEl) {
                return moduleEl.offsetTop - offset;
            }
            return 0;
        });
        const handlerScroll = () => {
            const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;

            // 如果滚动到底部，直接设置最后一个索引
            if (scrollTop + window.innerHeight >= scrollHeight) {
                setActiveIndex(list[list.length - 1].href);
                return;
            }

            // 二分查找优化索引查找过程
            let low = 0;
            let high = tops.length - 1;
            let mid = 0;
            while (low <= high) {
                mid = Math.floor((low + high) / 2);
                if (scrollTop < tops[mid]) {
                    high = mid - 1;
                } else if (scrollTop >= (tops[mid + 1] ?? scrollHeight)) {
                    low = mid + 1;
                } else {
                    break;
                }
            }

            // 更新活动索引
            if (mid !== -1 && !stopScroll.current) {
                setActiveIndex(list[mid].href);
                alignTab(list[mid].href);
            }
        };
        window.addEventListener('scroll', handlerScroll);
        return () => window.removeEventListener('scroll', handlerScroll);
    }, [list]);

    return (
        <div className={classNames(css['TheComp'])} style={{ top: stickyHeight, position: sticky ? 'sticky' : 'static' }}>
            <div className="tabs" ref={tabsRef}>
                {list.map((item) => (
                    <div
                        className={classNames('tab-item', `tab-item-${item.href}`, {
                            active: activeIndex === item.href,
                        })}
                        onClick={() => handleChange(item.href)}
                        key={item.href}
                    >
                        {item.title}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Tabs;
